import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_INVOICE_DESIGN,
  INVOICE_ACCENT_PRESETS,
  INVOICE_TEMPLATES,
  isHexColour,
  isTooLightForHeadings,
  type InvoiceTemplate,
} from '@tailoring-crm/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';
import { isApiRequestError } from '../../lib/api/errors.js';
import {
  useInvoiceDesignPreview,
  usePresentationSettings,
  useUpdateShopSettings,
} from '../../lib/settings/use-shop-settings.js';
import { InvoiceTemplateSketch } from './invoice-template-sketch.js';

/** How long typing must pause before the preview is re-rendered. A PDF per keystroke is a lot. */
export const PREVIEW_DEBOUNCE_MS = 400;

/** `#8B1E3F`, `8b1e3f` and ` #8b1e3f ` are the same colour; only the stored spelling differs. */
function normaliseHex(raw: string): string {
  const trimmed = raw.trim().toLowerCase();
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

/** `Forest green` → `forest_green`, the preset's key in the locale file. */
function presetKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z]+/g, '_');
}

/**
 * Which layout the shop's A4 bills are printed in, and in what colour.
 *
 * Saves on its own like the cards beside it — `{invoiceDesign}` and nothing else — and only on
 * Save. Choosing a card, a swatch or typing a hex changes what the PREVIEW shows, never what
 * is stored: the preview endpoint renders a design it is told about and keeps nothing.
 *
 * The colour is held twice: `hexText` is what is in the box, which may be half-typed; `accent`
 * is the last valid colour, which is what the sketches, the picker, the preview and Save use.
 * That is what lets an invalid box disable Save while the preview keeps showing the last good
 * bill, and what keeps the colour when Classic hides the control.
 */
export function BillDesignCard({ canWrite }: { canWrite: boolean }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const settings = usePresentationSettings();
  const update = useUpdateShopSettings();
  const stored = settings.data?.invoiceDesign ?? DEFAULT_INVOICE_DESIGN;
  const ids = useId();

  const [template, setTemplate] = useState<InvoiceTemplate>(stored.template);
  const [accent, setAccent] = useState(stored.accentColor);
  const [hexText, setHexText] = useState(stored.accentColor);
  const [previewAccent, setPreviewAccent] = useState(stored.accentColor);

  useEffect(() => {
    setTemplate(stored.template);
    setAccent(stored.accentColor);
    setHexText(stored.accentColor);
    setPreviewAccent(stored.accentColor);
  }, [stored.template, stored.accentColor]);

  // Typing and dragging the picker settle before the preview asks for another PDF. A preset
  // sets `previewAccent` itself, so a click is not made to wait.
  useEffect(() => {
    if (accent === previewAccent) return;
    const timer = setTimeout(() => setPreviewAccent(accent), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [accent, previewAccent]);

  const showColour = template !== 'classic';
  const hexValid = isHexColour(normaliseHex(hexText));
  const hexBlocksSave = showColour && !hexValid;
  const tooLight = showColour && hexValid && isTooLightForHeadings(accent);
  const dirty = template !== stored.template || accent !== stored.accentColor;

  const typeHex = (raw: string): void => {
    setHexText(raw);
    const hex = normaliseHex(raw);
    if (isHexColour(hex)) setAccent(hex);
  };

  const choosePreset = (hex: string): void => {
    setHexText(hex);
    setAccent(hex);
    setPreviewAccent(hex);
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (hexBlocksSave) return;
    void (async () => {
      try {
        await update.mutateAsync({ invoiceDesign: { template, accentColor: accent } });
        toast({ title: t('presentation.saved'), variant: 'success' });
      } catch (caught) {
        toast({
          title: isApiRequestError(caught) ? caught.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  const templatesLabel = `${ids}-templates`;
  const swatchesLabel = `${ids}-swatches`;
  const hexId = `${ids}-hex`;
  const hexMessage = `${ids}-hex-message`;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('presentation.billDesign.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <p className="text-sm text-muted-foreground">{t('presentation.billDesign.hint')}</p>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="flex min-w-0 flex-col gap-4">
              <div className="flex flex-col gap-2">
                <p id={templatesLabel} className="text-sm font-medium">
                  {t('presentation.billDesign.layout')}
                </p>
                {/*
                  Native radios, visually hidden, inside the cards. The browser then does the
                  radio group for free — one tab stop, arrow keys between options, the selected
                  one announced — which a hand-rolled roving tabindex only ever approximates.
                */}
                <div
                  role="radiogroup"
                  aria-labelledby={templatesLabel}
                  className="grid grid-cols-2 gap-2 sm:grid-cols-3"
                >
                  {INVOICE_TEMPLATES.map((one) => {
                    const nameId = `${ids}-template-${one}`;
                    return (
                      <label
                        key={one}
                        className="min-w-0 cursor-pointer has-[:disabled]:cursor-default"
                      >
                        <input
                          type="radio"
                          name={`${ids}-template`}
                          value={one}
                          checked={template === one}
                          disabled={!canWrite}
                          onChange={() => setTemplate(one)}
                          aria-labelledby={nameId}
                          aria-describedby={`${nameId}-description`}
                          className="peer sr-only"
                        />
                        <div className="flex h-full flex-col gap-2 rounded-md border border-border p-2 transition-colors peer-checked:border-primary peer-checked:bg-primary/5 peer-checked:ring-2 peer-checked:ring-primary peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2">
                          <div className="mx-auto w-full max-w-[96px]">
                            <InvoiceTemplateSketch template={one} accent={accent} />
                          </div>
                          <span id={nameId} className="text-sm font-semibold">
                            {t(`presentation.billDesign.template.${one}.name`)}
                          </span>
                          <span
                            id={`${nameId}-description`}
                            className="text-xs text-muted-foreground"
                          >
                            {t(`presentation.billDesign.template.${one}.description`)}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Classic is greyscale, so it has no colour to choose. The accent is kept, not
                  cleared, so switching back to Modern finds the colour where it was left. */}
              {showColour ? (
                <div className="flex flex-col gap-3">
                  <p id={swatchesLabel} className="text-sm font-medium">
                    {t('presentation.billDesign.accent')}
                  </p>
                  <div
                    role="radiogroup"
                    aria-labelledby={swatchesLabel}
                    className="flex flex-wrap gap-2"
                  >
                    {INVOICE_ACCENT_PRESETS.map((preset) => (
                      <label
                        key={preset.hex}
                        className="flex w-20 cursor-pointer flex-col items-center gap-1 text-center has-[:disabled]:cursor-default"
                      >
                        <input
                          type="radio"
                          name={`${ids}-accent`}
                          value={preset.hex}
                          checked={accent === preset.hex}
                          disabled={!canWrite}
                          onChange={() => choosePreset(preset.hex)}
                          className="peer sr-only"
                        />
                        <span
                          aria-hidden="true"
                          className="h-8 w-8 rounded-full border border-border ring-offset-2 ring-offset-background peer-checked:ring-2 peer-checked:ring-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring"
                          style={{ background: preset.hex }}
                        />
                        <span className="text-xs leading-tight">
                          {t(`presentation.billDesign.presets.${presetKey(preset.name)}`, {
                            defaultValue: preset.name,
                          })}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={hexId}>{t('presentation.billDesign.custom')}</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id={hexId}
                        value={hexText}
                        maxLength={7}
                        spellCheck={false}
                        autoComplete="off"
                        disabled={!canWrite}
                        aria-invalid={!hexValid}
                        aria-describedby={hexValid && !tooLight ? undefined : hexMessage}
                        onChange={(event) => typeHex(event.target.value)}
                        className="w-32 font-mono"
                      />
                      <input
                        type="color"
                        aria-label={t('presentation.billDesign.picker')}
                        value={accent}
                        disabled={!canWrite}
                        onChange={(event) => typeHex(event.target.value)}
                        className="h-11 w-14 cursor-pointer rounded-md border border-input bg-background p-1 disabled:cursor-default"
                      />
                    </div>
                    {!hexValid ? (
                      <p id={hexMessage} role="alert" className="text-sm text-destructive">
                        {t('presentation.billDesign.invalidHex')}
                      </p>
                    ) : tooLight ? (
                      // A warning, not a refusal: the shop may know its printer better than we do.
                      <p
                        id={hexMessage}
                        role="status"
                        className="rounded-md bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100"
                      >
                        {t('presentation.billDesign.tooLight')}
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            {canWrite ? (
              <BillDesignPreview template={template} accent={previewAccent} />
            ) : (
              // The sample bill carries the shop's bank details, and the API shows it only to
              // whoever may change the design.
              <p className="text-sm text-muted-foreground">
                {t('presentation.billDesign.previewOwnerOnly')}
              </p>
            )}
          </div>

          {canWrite ? (
            <div>
              <Button type="submit" disabled={update.isPending || !dirty || hexBlocksSave}>
                {t('common.save')}
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * The sample bill, as the server would print it, for the design on screen.
 *
 * An object URL over a fetched blob rather than the endpoint as `src` (see
 * `useInvoiceDesignPreview`). `data-request-url` carries the address the PDF was asked for, so
 * what the frame is showing stays inspectable even though its `src` is a `blob:` URL.
 */
function BillDesignPreview({
  template,
  accent,
}: {
  template: InvoiceTemplate;
  accent: string;
}): ReactElement {
  const { t } = useTranslation();
  const query = useInvoiceDesignPreview(template, accent, true);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  // One object URL per PDF, revoked when the next one replaces it or the card goes away. While
  // the next design renders, `query.data` is still the previous PDF, so nothing changes here and
  // the last good bill stays up.
  useEffect(() => {
    if (!query.data || typeof URL.createObjectURL !== 'function') return;
    const url = URL.createObjectURL(query.data.pdf);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [query.data]);

  const failed = query.isError && !query.isFetching;

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <p className="text-sm font-medium">{t('presentation.billDesign.preview')}</p>
      <div className="relative h-[480px] w-full overflow-hidden rounded-md border border-border bg-muted sm:h-[600px]">
        {failed ? (
          <div
            role="alert"
            className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center text-sm"
          >
            <p>{t('presentation.billDesign.previewFailed')}</p>
            <Button type="button" variant="outline" onClick={() => void query.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        ) : (
          <>
            {objectUrl ? (
              <iframe
                title={t('presentation.billDesign.previewTitle')}
                src={objectUrl}
                data-request-url={query.data?.requestUrl}
                className="h-full w-full bg-white"
              />
            ) : null}
            {query.isFetching ? (
              <div
                role="status"
                className="absolute inset-0 flex items-center justify-center bg-background/70 text-sm"
              >
                {t('presentation.billDesign.previewLoading')}
              </div>
            ) : null}
          </>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{t('presentation.billDesign.previewHint')}</p>
    </div>
  );
}
